import { business, personal, SupplierContext } from '@shared/components/suppliers/SupplierContext';
import { SupplierStatus } from '@shared/components/suppliers/SupplierStatus';
import { dispatch } from '@shared/redux/store';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { actions as modalActions } from '../../../../../Broadband/js/redux/modalSlice';
import { SearchFilterContext } from '../../../../../Broadband/React/js/components/filters/SearchFilterContext';
import SuppliersList from './SuppliersList';
import SuppliersSection from './SuppliersSection';

function parseCommaSeparatedQueryParam(search: string, key: string): string[] {
    if (!search) return [];
    const raw = new URLSearchParams(search).get(key);
    return raw
        ? raw
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
        : [];
}

function useQueryStringValues(key: string): string[] {
    const search = typeof window === 'undefined' ? '' : window.location.search;
    return useMemo(() => parseCommaSeparatedQueryParam(search, key), [search, key]);
}

export default function SupplierTab() {
    const searchContext = useContext(SearchFilterContext);
    const supplierContext = useContext(SupplierContext);
    // Use allItems so modal can show/tick suppliers even if not currently in result-set items
    const supplierItems =
        supplierContext.suppliers.allItems?.length > 0
            ? supplierContext.suppliers.allItems
            : supplierContext.suppliers.items;
    const suppliers = useMemo(
        () => supplierItems.slice().sort((a, b) => a.name.localeCompare(b.name)),
        [supplierItems]
    );

    const availableSuppliers = useMemo(() => suppliers.filter(p => p.isAvailable), [suppliers]);
    const personalSuppliers = useMemo(() => availableSuppliers.filter(p => p.isPersonal), [availableSuppliers]);
    const businessSuppliers = useMemo(() => availableSuppliers.filter(p => p.isBusiness), [availableSuppliers]);
    const isBusinessAvailable =
        searchContext.data.customerTypes.some(ct => ct.toLocaleLowerCase() === business) &&
        businessSuppliers.length > 0;
    const customerType = searchContext.criteria.common.customerType;
    const customerTypeNormalized = (customerType ?? '').toLowerCase();

    const [isAllPersonalSelected, setIsAllPersonalSelected] = useState<boolean>(true);
    const [isAllBusinessSelected, setIsAllBusinessSelected] = useState<boolean>(false);

    const selectedSupplierValuesFromCriteria = searchContext.criteria.common.suppliers?.values ?? [];
    const supplierValuesFromUrl = useQueryStringValues('supplier');
    const selectedSupplierValues =
        selectedSupplierValuesFromCriteria.length > 0 ? selectedSupplierValuesFromCriteria : supplierValuesFromUrl;
    const selectedSupplierKeySet = new Set(selectedSupplierValues.map(v => v.toLowerCase()));
    const isNothingSelected = selectedSupplierValues.length === 0;
    const [personalSupplierStatuses, setPersonalSupplierStatuses] = useState<SupplierStatus[]>(
        personalSuppliers.map<SupplierStatus>(s => ({
            supplier: s,
            checked:
                (isNothingSelected && (customerTypeNormalized === '' || customerTypeNormalized === personal)) ||
                (selectedSupplierKeySet.has(s.shortUrl.toLowerCase()) &&
                    (customerTypeNormalized === '' || customerTypeNormalized === personal))
        }))
    );

    const [businessSupplierStatuses, setBusinessSupplierStatuses] = useState<SupplierStatus[]>(
        businessSuppliers.map<SupplierStatus>(s => ({
            supplier: s,
            checked:
                (isNothingSelected && customerTypeNormalized === business) ||
                (selectedSupplierKeySet.has(s.shortUrl.toLowerCase()) &&
                    (customerTypeNormalized === '' || customerTypeNormalized === business))
        }))
    );

    // Sync checkbox state when context criteria (e.g. from popular provider selection) changes
    const contextCustomerType = searchContext.criteria.common.customerType ?? '';
    const contextCustomerTypeNormalized = contextCustomerType.toLowerCase();
    const supplierValuesKey = useMemo(
        () => (selectedSupplierValues.length === 0 ? '' : selectedSupplierValues.slice().sort().join(',')),
        [selectedSupplierValues]
    );
    const supplierValuesForEffect = useMemo(
        () => (supplierValuesKey === '' ? [] : supplierValuesKey.split(',')),
        [supplierValuesKey]
    );
    const personalSuppliersKey = useMemo(
        () =>
            personalSuppliers.length === 0
                ? ''
                : personalSuppliers
                      .map(s => s.shortUrl)
                      .slice()
                      .sort()
                      .join(','),
        [personalSuppliers]
    );
    const businessSuppliersKey = useMemo(
        () =>
            businessSuppliers.length === 0
                ? ''
                : businessSuppliers
                      .map(s => s.shortUrl)
                      .slice()
                      .sort()
                      .join(','),
        [businessSuppliers]
    );
    useEffect(() => {
        const isNothingSelected = supplierValuesForEffect.length === 0;
        const supplierKeySet = new Set(supplierValuesForEffect.map(v => v.toLowerCase()));
        setPersonalSupplierStatuses(
            personalSuppliers.map<SupplierStatus>(s => ({
                supplier: s,
                checked:
                    (isNothingSelected &&
                        (contextCustomerTypeNormalized === '' || contextCustomerTypeNormalized === personal)) ||
                    (supplierKeySet.has(s.shortUrl.toLowerCase()) &&
                        (contextCustomerTypeNormalized === '' || contextCustomerTypeNormalized === personal))
            }))
        );
        setBusinessSupplierStatuses(
            businessSuppliers.map<SupplierStatus>(s => ({
                supplier: s,
                checked:
                    (isNothingSelected && contextCustomerTypeNormalized === business) ||
                    (supplierKeySet.has(s.shortUrl.toLowerCase()) &&
                        (contextCustomerTypeNormalized === '' || contextCustomerTypeNormalized === business))
            }))
        );
    }, [
        supplierValuesForEffect,
        contextCustomerTypeNormalized,
        personalSuppliers,
        businessSuppliers,
        personalSuppliersKey,
        businessSuppliersKey
    ]);

    useEffect(() => {
        const personalAllSelected = personalSupplierStatuses
            .filter(ss => ss.supplier.isPersonal)
            .every(ss => ss.checked);
        setIsAllPersonalSelected(personalAllSelected);
        setIsAllBusinessSelected(businessSupplierStatuses.filter(ss => ss.supplier.isBusiness).every(ss => ss.checked));

        const customerType =
            personalSupplierStatuses.some(ss => ss.checked) && businessSupplierStatuses.some(ss => ss.checked)
                ? ''
                : personalSupplierStatuses.some(ss => ss.checked)
                  ? personal
                  : businessSupplierStatuses.some(ss => ss.checked)
                    ? business
                    : personal;

        if (personalAllSelected && customerType === personal) {
            dispatch(modalActions.updateSelectedSuppliers({ keys: [], customerType: personal }));
            return;
        }

        const businessAllSelected = businessSupplierStatuses
            .filter(ss => ss.supplier.isBusiness)
            .every(ss => ss.checked);
        if (businessAllSelected && customerType === business) {
            dispatch(modalActions.updateSelectedSuppliers({ keys: [], customerType: business }));
            return;
        }

        dispatch(
            modalActions.updateSelectedSuppliers({
                keys: personalSupplierStatuses
                    .concat(businessSupplierStatuses)
                    .filter(ss => ss.checked)
                    .map(ss => ss.supplier.shortUrl),
                customerType: customerType
            })
        );
    }, [personalSupplierStatuses, businessSupplierStatuses]);

    const OnSelectChange = useCallback(
        (sender, isBusiness) => {
            if (isBusiness) {
                const isAllState = sender.target.checked;
                const newSupplierStatuses = [...businessSupplierStatuses];
                newSupplierStatuses.forEach(ss => (ss.checked = isAllState));
                setBusinessSupplierStatuses(newSupplierStatuses);
            } else {
                // Personal suppliers

                const isAllState = sender.target.checked;
                const newSupplierStatuses = [...personalSupplierStatuses];
                newSupplierStatuses.forEach(ss => (ss.checked = isAllState));
                setPersonalSupplierStatuses(newSupplierStatuses);
            }
        },
        [personalSupplierStatuses, businessSupplierStatuses]
    );

    return (
        <React.Fragment>
            <div>
                <div className="row pad-t-2 pad-b-3">
                    <div className="col-xs-24">
                        <h5 className="font-6 mar-0">
                            {searchContext.criteria.common.address
                                ? searchContext.data.resources.providersInYourArea
                                : searchContext.data.resources.providers}
                        </h5>
                        <SuppliersSection
                            supplierStatuses={personalSupplierStatuses}
                            statusChanger={setPersonalSupplierStatuses}
                            isBusiness={false}
                            isAllSelected={isAllPersonalSelected}
                            OnSelectChange={OnSelectChange}
                            isVerticallyAligned={true}
                        />
                        {(() => {
                            const unavailableSuppliers = suppliers.filter(s => !s.isAvailable);
                            if (unavailableSuppliers.length) {
                                return (
                                    <div className="col-xs-24 pad-y-2 bor-t-1 mar-t-2">
                                        <button
                                            className="btn btn-link btn-block"
                                            type="button"
                                            data-toggle="collapse"
                                            data-target="#collapseUnavailableProviders"
                                            aria-controls="collapseUnavailableProviders"
                                        >
                                            {searchContext.data.resources.unavailableSuppliersInYourArea}{' '}
                                            <span className="fa fa-chevron-down font-5"></span>
                                        </button>
                                        <div className="collapse in" id="collapseUnavailableProviders">
                                            <SuppliersList
                                                className="supplier-list mar-y-3 block-grid-xs-2  block-grid-md-4"
                                                supplierStatuses={unavailableSuppliers.map(s => ({
                                                    supplier: s,
                                                    checked: selectedSupplierKeySet.has(s.shortUrl.toLowerCase())
                                                }))}
                                                disabled={true}
                                                statusChanger={null}
                                                suffix="non-available"
                                                isVerticallyAligned={false}
                                            />
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        {isBusinessAvailable ? (
                            <SuppliersSection
                                supplierStatuses={businessSupplierStatuses}
                                statusChanger={setBusinessSupplierStatuses}
                                isBusiness={true}
                                isAllSelected={isAllBusinessSelected}
                                OnSelectChange={OnSelectChange}
                                isVerticallyAligned={true}
                            />
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}
